import { useEffect, useState } from "react";
import {
	Col,
	Container,
	FormControl,
	InputGroup,
	Row,
	Spinner,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { FaSearch } from "react-icons/fa";
import { MdPersonSearch } from "react-icons/md";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { Modal } from "../../../../components/modal";
import { PaginationList } from "../../../../components/pagination";
import { IUserManager } from "../../../../models/IUserManager";
import {
	cancelSubscription,
	getSubscriptions,
	inactivateSubscription,
	reactivateSubscription,
} from "../../../../services/manager/signature.service";
import { SubscriptionsTable } from "../../components/table-subscriptions";
import { ContentBody, ContentHeader } from "../../styled";

interface ISubscriptions {}

/**
 *
 * @param props
 * @returns
 */
export const Subscriptions: React.FC<ISubscriptions> = (props) => {
	const { t: translate } = useTranslation();
	const history = useHistory();

	//Pagination
	const [totalItens, setTotalItens] = useState(0);
	const [currentPage, setCurrentPage] = useState(1);

	//Ordering
	const [sortOrder, setSortOrder] = useState<1 | 0 | -1>(1);
	const [sortField, setSortField] = useState("name");

	const [query, setQuery] = useState("");

	const [loading, setLoading] = useState<boolean>(true);
	const [loadedOnce, setLoadedOnce] = useState(false);
	const [usersSubscribersList, setUsersSubscribersList] = useState([]);

	// modals
	const [user, setUser] = useState<IUserManager | null>(null);
	const [modalIsLoading, setModalIsLoading] = useState(false);
	const [showCancelModal, setShowCancelModal] = useState(false);
	const [showInactivateModal, setShowInactivateModal] = useState(false);
	const [showReactivateModal, setShowReactivateModal] = useState(false);

	const getSubscribersList = (_query?: string) => {
		setLoading(true);

		getSubscriptions(_query).then((data) => {
			setUsersSubscribersList(data.results);

			setTotalItens(data.count);
			setLoading(false);

			if (!loadedOnce) {
				setLoadedOnce(true);
			}
		});
	};

	const getSubscribersListWithParams = () => {
		const order = sortOrder > 0 ? sortField : "-" + sortField;

		getSubscribersList(`page=${currentPage}&q=${query}&ordering=${order}`);
	};

	// realiza uma busca quando mudar a query, e quando entrar na pagina
	useEffect(() => {
		delayedSearch();
	}, [query]);

	const delayedSearch = useDebounce(() => {
		getSubscribersListWithParams();
	}, 800);

	const handleTableSort = (event: any) => {
		let order =
			event.sortOrder > 0 ? event.sortField : "-" + event.sortField;

		setSortOrder(event.sortOrder);
		setSortField(event.sortField);

		getSubscribersList(`page=${currentPage}&q=${query}&ordering=${order}`);
	};

	const handlePageChange = (event?: any) => {
		let order = sortOrder > 0 ? sortField : "-" + sortField;

		if (currentPage !== event) {
			getSubscribersList(`page=${event}&q=${query}&ordering=${order}`);

			setCurrentPage(event);
		}

		window.scrollTo(0, 0);
	};

	const handleCancelSubscription = (_user: IUserManager) => {
		setUser(_user);
		setShowCancelModal(true);
	};

	const handleInactivateSubscription = (_user: any) => {
		setUser(_user);
		setShowInactivateModal(true);
	};

	const handleReactivateSubscription = (_user: any) => {
		setUser(_user);
		setShowReactivateModal(true);
	};

	const onSubmitCancelSubscription = () => {
		if (user) {
			setModalIsLoading(true);

			cancelSubscription(user.signature!.id)
				.then(() => {
					toast.success(`Assinatura cancelada com sucesso!`);

					getSubscribersListWithParams();
				})
				.catch((error: any) => {
					console.log(error);
					toast.error(`Erro ao cancelar assinatura!`);
				})
				.finally(() => {
					setShowCancelModal(false);
					setModalIsLoading(false);
				});
		}
	};

	const onSubmitInactivateSubscription = () => {
		if (user) {
			setModalIsLoading(true);

			inactivateSubscription(user.signature!.id)
				.then(() => {
					toast.success(`Assinatura inativada com sucesso!`);

					getSubscribersListWithParams();
				})
				.catch((error: any) => {
					console.log(error);
					toast.error(`Erro ao inativar assinatura!`);
				})
				.finally(() => {
					setShowInactivateModal(false);
					setModalIsLoading(false);
				});
		}
	};

	const onSubmitReactivateSubscription = () => {
		if (user) {
			setModalIsLoading(true);

			reactivateSubscription(user.signature!.id)
				.then(() => {
					toast.success(`Assinatura reativada com sucesso!`);

					getSubscribersListWithParams();
				})
				.catch((error: any) => {
					console.log(error);
					toast.error(`Erro ao reativar assinatura!`);
				})
				.finally(() => {
					setShowReactivateModal(false);
					setModalIsLoading(false);
				});
		}
	};

	const handleUserDetail = (node?: any) => {
		if (node && node.id) {
			history.push(`/manager/user/${node.id}`);
		}
	};

	return (
		<Container fluid>
			<ContentHeader>
				<Row>
					<div className="title">
						{translate("manager.subscription.title")}
						<span className="subtitle">
							{totalItens} resultados
						</span>
					</div>
				</Row>
			</ContentHeader>
			{/*  */}
			<ContentBody>
				<Row className="mt-4">
					<Col
						xs={12}
						md={5}
					>
						<InputGroup className="ds-input-group">
							<InputGroup.Text>
								<FaSearch></FaSearch>
							</InputGroup.Text>
							<FormControl
								className="input-text"
								placeholder={translate(
									"manager.subscription.search",
								)}
								aria-label={translate(
									"manager.subscription.search",
								)}
								value={query}
								onChange={(event: any) =>
									setQuery(event.target.value)
								}
							/>
						</InputGroup>
					</Col>
				</Row>

				{!loadedOnce ? (
					<div className="loading-container mt-4">
						<Spinner
							animation="border"
							variant="primary"
							style={{
								width: "2rem",
								height: "2rem",
								borderWidth: "0.25em",
								borderRightColor: "transparent",
							}}
						/>
					</div>
				) : (
					<>
						<Row className="mt-3">
							<>
								{!loading && totalItens === 0 ? (
									<div className="text-center text-grey mt-5">
										<MdPersonSearch
											className="mt-5"
											size={100}
										/>
										<h5>
											{translate(
												"manager.user.emptySubscriber",
											)}
										</h5>
									</div>
								) : (
									<SubscriptionsTable
										loading={loading}
										nodes={usersSubscribersList}
										sortField={sortField}
										sortOrder={sortOrder}
										handleSort={handleTableSort}
										handleCancelSubscription={
											handleCancelSubscription
										}
										handleInactivateSubscription={
											handleInactivateSubscription
										}
										handleViewProfile={handleUserDetail}
										handleReactivateSubscription={
											handleReactivateSubscription
										}
									/>
								)}
							</>
						</Row>
						<Row className="mt-5">
							<PaginationList
								currentPage={currentPage}
								totalItems={totalItens}
								onPageChange={handlePageChange}
							></PaginationList>
						</Row>
					</>
				)}
			</ContentBody>

			{/* <ModalSignature
				showModal={showModalSignature}
				setShowModal={setShowModalSignature}
				isNewSignture={isRenewSignature}
				user={user}
				loading={loadingModal}
				setLoading={setLoadingModal}
			/> */}

			{/* Cancelar Assinatura */}
			<Modal
				headerTitle="Cancelar Assinatura"
				submitButtonText={translate("manager.user.confirm")}
				show={showCancelModal}
				onHide={() => setShowCancelModal(false)}
				onSubmit={onSubmitCancelSubscription}
				variant="confirm-modal-default"
				loading={modalIsLoading}
			>
				<p>
					Tem certeza que deseja <b>cancelar</b> a assinatura do
					usuário<b> {user?.name ?? ""}</b>?
				</p>
			</Modal>

			{/* Inativar Assinatura */}
			<Modal
				headerTitle="Inativar Assinatura"
				submitButtonText={translate("manager.user.confirm")}
				show={showInactivateModal}
				onHide={() => setShowInactivateModal(false)}
				onSubmit={onSubmitInactivateSubscription}
				variant="confirm-modal-default"
				loading={modalIsLoading}
			>
				<p>
					Tem certeza que deseja <b>inativar</b> a assinatura do
					usuário<b> {user?.name ?? ""}</b>?
				</p>
			</Modal>

			{/* Reativar Assinatura */}
			<Modal
				headerTitle="Reativar Assinatura"
				submitButtonText={translate("manager.user.confirm")}
				show={showReactivateModal}
				onHide={() => setShowReactivateModal(false)}
				onSubmit={onSubmitReactivateSubscription}
				variant="confirm-modal-default"
				loading={modalIsLoading}
			>
				<p>
					Tem certeza que deseja <b>reativar</b> a assinatura do
					usuário<b> {user?.name ?? ""}</b>?
				</p>
			</Modal>
		</Container>
	);
};
